import { isoKw, letzteTage } from '@netcon-work/shared'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import {
  erledigtProTagQuery,
  flussQuery,
  liegezeitenQuery,
  zeitleisteQuery,
  type LeitstandHeute,
} from '../../lib/queries'
import { trpc } from '../../lib/trpc'
import { useAbwesenheiten } from '../Abwesenheit'
import { AufgabenPopup } from '../AufgabenPopup'
import { AktivitaetsZeitleiste } from '../diagramm/AktivitaetsZeitleiste'
import { BalkenErledigt } from '../diagramm/BalkenErledigt'
import { LiegezeitenMatrix } from '../diagramm/LiegezeitenMatrix'
import { TagesPopup, type TagesAuswahl } from '../TagesPopup'
import { Card, CardPad } from '../ui/primitives'

export const ZEITRAEUME = ['7', '14', '30', '90'] as const
export type Zeitraum = (typeof ZEITRAEUME)[number]

/** Wie viele Wochen das Fluss-Diagramm zu einem Zeitraum zeigt – nie weniger als vier. */
const WOCHEN: Record<Zeitraum, number> = { '7': 4, '14': 6, '30': 8, '90': 13 }

/** Ab hier wird nicht vorgehalten – der lange Rückblick lädt erst beim Umschalten (Leitplanke 4). */
const VORGEHALTEN = 30

type Woche = Awaited<ReturnType<typeof trpc.analytics.fluss.query>>[number]

const kw = (montag: string) => `KW ${isoKw(montag).slice(-2)}`

/**
 * **Reiter „Rückblick"** – wie die Arbeit gelaufen ist ([E-358](../../../../../docs/ENTSCHEIDUNGEN.md#e-358)).
 *
 * Der einzige Reiter mit Zeitraum, und der Umschalter wirkt auf **alles** darin. Im alten
 * Leitstand stand er oben über der ganzen Seite und wirkte nur auf zwei von sieben Blöcken.
 */
export function LeitstandRueckblick({
  zeitraum,
  heute,
}: {
  zeitraum: Zeitraum
  /** Für die Personenauswahl der Aktivität: nur Mitglieder dieser Arbeitswelt. */
  heute: LeitstandHeute | undefined
}) {
  const queryClient = useQueryClient()
  const tage = Number(zeitraum)
  const spanne = letzteTage(tage)
  const von = spanne[0] as string
  const bis = spanne[spanne.length - 1] as string
  const [auswahl, setAuswahl] = useState<TagesAuswahl | null>(null)
  const [popup, setPopup] = useState<{ titel: string; ids: string[] } | null>(null)

  const { data: fluss } = useQuery(flussQuery(WOCHEN[zeitraum]))
  const { data: erledigt } = useQuery(erledigtProTagQuery(tage))
  const { data: zeitleiste } = useQuery(zeitleisteQuery(von, bis))
  const { data: liegezeiten = [] } = useQuery(liegezeitenQuery)
  const abwesenheiten = useAbwesenheiten()

  /** Die anderen kurzen Zeiträume vorhalten, damit Umschalten lokal bleibt (Leitplanke 2). */
  useEffect(() => {
    for (const z of ZEITRAEUME) {
      const n = Number(z)
      if (n === tage || n > VORGEHALTEN) continue
      const andere = letzteTage(n)
      void queryClient.prefetchQuery(erledigtProTagQuery(n))
      void queryClient.prefetchQuery(
        zeitleisteQuery(andere[0] as string, andere[andere.length - 1] as string),
      )
      void queryClient.prefetchQuery(flussQuery(WOCHEN[z]))
    }
  }, [queryClient, tage])

  /**
   * **Nur Menschen dieser Arbeitswelt.** Die Zeitleiste zählte jeden Akteur im Log – auch
   * die Konten „System" und „Portierung", die Importe schreiben. Maßgeblich ist dieselbe
   * Menge wie in der Team-Tabelle.
   */
  const mitglieder = heute
    ? new Set([
        ...heute.team.zeilen.flatMap((z) => (z.personId ? [z.personId] : [])),
        ...heute.team.ohneArbeit.map((p) => p.personId),
      ])
    : null
  const personen = (zeitleiste?.personen ?? []).filter(
    (p) => p.gesamt > 0 && (mitglieder === null || mitglieder.has(p.personId)),
  )

  return (
    <div className="grid gap-4">
      <Trends wochen={fluss} />

      <div className="grid items-start gap-4 xl:grid-cols-[minmax(0,1.25fr)_minmax(0,1fr)]">
        <Card>
          <CardPad>
            <div className="flex flex-wrap items-baseline gap-2">
              <b className="text-[13.5px]">Zufluss und Abfluss</b>
              <span className="text-xs text-muted">
                je Woche · Klick auf einen Balken zeigt die Aufgaben
              </span>
            </div>
            {fluss ? (
              <FlussDiagramm wochen={fluss} zeigen={(titel, ids) => setPopup({ titel, ids })} />
            ) : (
              <div className="py-2 text-[13px] text-ink2">Lädt …</div>
            )}
          </CardPad>
        </Card>
        <Card>
          <CardPad>
            <LiegezeitenMatrix zeilen={liegezeiten} />
          </CardPad>
        </Card>
      </div>

      <Card>
        <CardPad>
          {erledigt ? (
            <BalkenErledigt
              tage={erledigt.team}
              onTagKlick={(tag) => setAuswahl({ personId: null, name: 'Team', tag })}
            />
          ) : (
            <div className="py-2 text-[13px] text-ink2">Lädt …</div>
          )}
        </CardPad>
      </Card>

      <Card>
        <CardPad>
          <div className="mb-1 flex flex-wrap items-baseline gap-2">
            <b className="text-[13.5px]">Aktivität</b>
            <span className="text-xs text-muted">
              wer war an welchem Tag womit beschäftigt · Zelle öffnet den Tag
            </span>
          </div>
          {zeitleiste ? (
            <AktivitaetsZeitleiste
              tage={zeitleiste.tage}
              personen={personen}
              abwesendAn={abwesenheiten.abwesendAn}
              onZelleKlick={(personId, name, tag) => setAuswahl({ personId, name, tag })}
            />
          ) : (
            <div className="py-2 text-[13px] text-ink2">Lädt …</div>
          )}
        </CardPad>
      </Card>

      {auswahl && <TagesPopup auswahl={auswahl} schliessen={() => setAuswahl(null)} />}
      {popup && (
        <AufgabenPopup
          titel={popup.titel}
          untertitel={`${popup.ids.length} ${popup.ids.length === 1 ? 'Aufgabe' : 'Aufgaben'}`}
          gruppen={[{ ids: popup.ids }]}
          schliessen={() => setPopup(null)}
        />
      )}
    </div>
  )
}

/**
 * **Drei Trends statt vier Bestandszahlen.** Verglichen wird die **letzte volle Woche** mit
 * der davor: die laufende ist am Donnerstag erst halb, und ein „▼ 12 ggü. Vorwoche" am
 * Mittwochmorgen wäre eine Warnung, die nur der Kalender auslöst.
 */
function Trends({ wochen }: { wochen: Woche[] | undefined }) {
  const voll = wochen && wochen.length >= 3 ? wochen.slice(0, -1) : null
  const letzte = voll?.[voll.length - 1]
  const davor = voll?.[voll.length - 2]
  const mittel = (f: (w: Woche) => number) =>
    voll ? Math.round((voll.reduce((n, w) => n + f(w), 0) / voll.length) * 10) / 10 : null

  return (
    <div className="grid gap-2.5 md:grid-cols-3">
      <TrendKachel
        label={letzte ? `Erledigt · ${kw(letzte.montag)}` : 'Erledigt je Woche'}
        wert={letzte?.erledigt.length}
        vorher={davor?.erledigt.length}
        mehrIstGut
        zusatz={mittel((w) => w.erledigt.length)}
        zusatzText="im Mittel der vollen Wochen"
      />
      <TrendKachel
        label={letzte ? `Neu hereingekommen · ${kw(letzte.montag)}` : 'Neu je Woche'}
        wert={letzte?.neu.length}
        vorher={davor?.neu.length}
        zusatz={letzte ? letzte.neu.length - letzte.erledigt.length : null}
        zusatzText="mehr hinein als hinaus"
        bilanz
      />
      <TrendKachel
        label={letzte ? `Durchlaufzeit, Median · ${kw(letzte.montag)}` : 'Durchlaufzeit, Median'}
        wert={letzte?.durchlaufMedian ?? undefined}
        vorher={davor?.durchlaufMedian ?? undefined}
        einheit=" T"
        zusatz={letzte?.durchlaufBasis ?? null}
        zusatzText="erledigte mit belegtem Arbeitsbeginn"
      />
    </div>
  )
}

function TrendKachel({
  label,
  wert,
  vorher,
  einheit = '',
  mehrIstGut = false,
  zusatz,
  zusatzText,
  bilanz = false,
}: {
  label: string
  wert: number | undefined
  vorher: number | undefined
  einheit?: string
  /** Bei Erledigt ist mehr gut; bei Zufluss und Durchlaufzeit ist mehr schlecht. */
  mehrIstGut?: boolean
  zusatz: number | null
  zusatzText: string
  /** Die Zufluss-Kachel sagt die Bilanz als Satz statt als Mittelwert. */
  bilanz?: boolean
}) {
  const diff =
    wert !== undefined && vorher !== undefined ? Math.round((wert - vorher) * 10) / 10 : null
  const gut = diff === null || diff === 0 ? null : diff > 0 === mehrIstGut
  const zahl = (n: number) => n.toLocaleString('de-DE')
  return (
    <Card>
      <div className="grid gap-0.5 px-4 py-3">
        <span className="text-[11.5px] font-medium text-ink2">{label}</span>
        <span className="flex items-baseline gap-2 text-[24px] font-bold tracking-[-0.5px]">
          {wert === undefined ? <span className="text-muted">–</span> : `${zahl(wert)}${einheit}`}
          {diff !== null && diff !== 0 && (
            <span
              className={`text-xs font-semibold tracking-normal ${gut ? 'text-[#1d6b34]' : 'text-[#b02a2a]'}`}
            >
              {diff > 0 ? '▲' : '▼'} {zahl(Math.abs(diff))}
              {einheit} ggü. Vorwoche
            </span>
          )}
        </span>
        <span className="text-[11px] text-muted">
          {zusatz === null
            ? ' '
            : bilanz
              ? zusatz > 0
                ? `${zusatz} mehr hinein als hinaus – der Bestand wächst`
                : zusatz < 0
                  ? `${-zusatz} mehr hinaus als hinein – der Bestand schrumpft`
                  : 'so viel hinein wie hinaus'
              : `${zahl(zusatz)} ${zusatzText}`}
        </span>
      </div>
    </Card>
  )
}

/**
 * **Zufluss und Abfluss als Balkenpaare.** Zwei Serien, zwei Farben: neu in einem warmen Grau
 * (es ist Bestand, keine Leistung), erledigt im Akzent. Die laufende Woche trägt „läuft" unter
 * der Beschriftung, damit ihr kurzer Balken nicht wie ein Einbruch gelesen wird.
 */
function FlussDiagramm({
  wochen,
  zeigen,
}: {
  wochen: Woche[]
  zeigen: (titel: string, ids: string[]) => void
}) {
  const B = 560
  const H = 170
  const oben = 14
  const links = 28
  const unten = 34
  const hoehe = H - oben - unten
  const hoechst = Math.max(4, ...wochen.flatMap((w) => [w.erledigt.length, w.neu.length]))
  const schritt = hoechst <= 10 ? 2 : hoechst <= 25 ? 5 : hoechst <= 60 ? 10 : 20
  const max = Math.ceil(hoechst / schritt) * schritt
  const y = (v: number) => oben + hoehe - (v / max) * hoehe
  const gruppe = (B - links) / wochen.length
  const breite = Math.min(18, gruppe / 2 - 3)
  const ticks = Array.from({ length: max / schritt + 1 }, (_, i) => i * schritt)

  return (
    <div className="overflow-x-auto">
      <svg
        viewBox={`0 0 ${B} ${H}`}
        className="mt-2 w-full max-w-[720px] min-w-[420px]"
        role="img"
        aria-label={`Neu und erledigt je Woche, ${kw(wochen[0]!.montag)} bis ${kw(wochen[wochen.length - 1]!.montag)}`}
      >
        {ticks.map((t) => (
          <g key={t}>
            <line x1={links} x2={B} y1={y(t)} y2={y(t)} style={{ stroke: 'var(--color-line)' }} />
            <text
              x={links - 6}
              y={y(t) + 3.5}
              fontSize={10}
              textAnchor="end"
              style={{ fill: 'var(--color-muted)' }}
            >
              {t}
            </text>
          </g>
        ))}
        {wochen.map((w, i) => {
          const mitte = links + i * gruppe + gruppe / 2
          const laeuft = i === wochen.length - 1
          return (
            <g key={w.montag}>
              <rect
                x={mitte - breite - 1}
                y={y(w.neu.length)}
                width={breite}
                height={oben + hoehe - y(w.neu.length)}
                rx={2}
                style={{ fill: '#c9c6bd', cursor: w.neu.length ? 'pointer' : 'default' }}
                onClick={() => w.neu.length && zeigen(`${kw(w.montag)} · neu`, w.neu)}
              >
                <title>{`${kw(w.montag)}: ${w.neu.length} neu`}</title>
              </rect>
              <rect
                x={mitte + 1}
                y={y(w.erledigt.length)}
                width={breite}
                height={oben + hoehe - y(w.erledigt.length)}
                rx={2}
                style={{
                  fill: 'var(--color-accent)',
                  cursor: w.erledigt.length ? 'pointer' : 'default',
                }}
                onClick={() =>
                  w.erledigt.length && zeigen(`${kw(w.montag)} · erledigt`, w.erledigt)
                }
              >
                <title>{`${kw(w.montag)}: ${w.erledigt.length} erledigt`}</title>
              </rect>
              <text
                x={mitte}
                y={oben + hoehe + 14}
                fontSize={10}
                textAnchor="middle"
                style={{ fill: laeuft ? 'var(--color-ink)' : 'var(--color-muted)' }}
                fontWeight={laeuft ? 600 : 400}
              >
                {kw(w.montag)}
              </text>
              {laeuft && (
                <text
                  x={mitte}
                  y={oben + hoehe + 26}
                  fontSize={9.5}
                  textAnchor="middle"
                  style={{ fill: 'var(--color-muted)' }}
                >
                  läuft
                </text>
              )}
            </g>
          )
        })}
      </svg>
      <div className="mt-1 flex gap-4 text-[11px] text-muted">
        <span className="inline-flex items-center gap-1.5">
          <i className="inline-block h-2 w-3.5 rounded-sm bg-accent" /> erledigt
        </span>
        <span className="inline-flex items-center gap-1.5">
          <i className="inline-block h-2 w-3.5 rounded-sm bg-[#c9c6bd]" /> neu
        </span>
      </div>
    </div>
  )
}
